import React, { useEffect, useState } from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { hashHistory } from 'react-router';

import { BackLink, Button, Typography } from '../../components/atoms';
import {
  Add as AddIcon,
  Location as LocationIcon,
  Profile as ProfileIcon,
} from '../../components/icons';
import { LoadingSpinner, Page } from '../../components/layouts';
import { ErrorFallback } from '../../components/layouts/ErrorFallback';
import { NavList } from '../../components/molecules';
import {
  AboutMyDataDialog,
  CampaignConsentDialog,
} from '../../components/partials';
import { PageHeader } from '../../components/partials/PageHeader';
import { DeliveryLocation } from '../../core/api/user/user.types';
import { analyticsService } from '../../core/services';
import { Status } from '../../core/types/data.types';
import { RouteParams } from '../../core/types/route.types';
import { domUtils, urlUtils } from '../../utils';
import { DispatchProps, StateProps } from '../../weborder/component/Delivery';

type Props = StateProps & DispatchProps & RouteParams;

const useStyles = makeStyles((theme: Theme) => ({
  aboutMyData: {
    paddingRight: theme.spacing(3),
    textAlign: 'right',
  },

  deliveryNavList: {
    marginBottom: theme.spacing(3),
  },

  locationItem: {
    padding: theme.spacing(0, 2),

    '& .secondary': {
      color: theme.palette.grey[800],
    },
  },

  [theme.breakpoints.up('md')]: {
    aboutMyData: {
      paddingRight: 0,
    },
  },
}));

export function DeliverySelectLocation(props: Props): JSX.Element {
  const classes = useStyles();
  const {
    appConfig,
    Customer: customer,
    deliveryLocations,
    deliverySummaries,
    deliveryOptions,
    isLoggedIn,
    LastRestaurantUrl: lastRestaurantUrl,
    params,
    preselectedStoreVrid,
    PushLoginUrl: pushLoginUrl,
    setPreselectedStoreVrid,
    translate,
  } = props;

  const [showAboutMyData, setShowAboutMyData] = useState(false);

  useEffect(() => {
    domUtils.skipToContent();
  }, []);

  useEffect(() => {
    pushLoginUrl();
  }, []);

  useEffect(() => {
    if (params.vrid) {
      const vrId = Number(params.vrid);

      const isPreselectedStoreVridValid =
        deliverySummaries.status === 'success' &&
        deliverySummaries.data.some(
          (store) => vrId === store.VirtualRestaurantId,
        );

      if (isPreselectedStoreVridValid) {
        setPreselectedStoreVrid(vrId);
      }
    }
  }, [deliverySummaries.status, params.vrid]);

  useEffect(() => {
    if (!appConfig.DeliveryEnabled) {
      urlUtils.navigateToHash('/where');
    }
  }, []);

  if (
    deliveryLocations.status === Status.ERROR ||
    deliverySummaries.status === 'none'
  ) {
    return <ErrorFallback logIdentifier="delivery-select-location" />;
  }

  if (
    deliveryLocations.status === Status.LOADING ||
    deliverySummaries.status === 'pending'
  ) {
    return <LoadingSpinner />;
  }

  const trackAddressSelected = (locationId: number): void => {
    const previousAddressCount = deliveryLocations.data.length;
    const selectedAddressPosition =
      deliveryLocations.data.findIndex(
        (address) => address.DeliveryLocationId === locationId,
      ) + 1;

    const eventData = {
      amp_label: 'Address Selected',
      amp_numberOfAddressesPresented: previousAddressCount,
      amp_positionOfSelectedAddress: selectedAddressPosition,
      amp_selectedPreviousOrderAddress: true,
    };

    analyticsService.track(eventData);
  };

  const trackBackSelected = (): void => {
    const eventData = {
      amp_label: 'Back Selected',
      amp_view: 'Delivery Select Location',
    };
    analyticsService.track(eventData);
  };

  const getLocationAddress = (
    deliveryLocation: DeliveryLocation,
  ): JSX.Element => {
    const addressLine1 = deliveryLocation.TwoLinesDisplay[0].trim();
    const addressLine2 = deliveryLocation.TwoLinesDisplay[1].trim();

    return (
      <div
        className={classes.locationItem}
        key={`delivery-address-${deliveryLocation.DeliveryLocationId}`}
      >
        <Typography>
          <em>{addressLine1}</em>
        </Typography>
        <Typography variant="body2-secondary">{addressLine2}</Typography>
      </div>
    );
  };

  const getLocationLoadingItem = () => ({
    elements: [
      <LocationIcon key="item-location-loading-icon" />,
      <Typography key="item-location-loading-text" variant="body2-secondary">
        <em>{translate('Loading_delivery_options')}</em>
      </Typography>,
    ],
    key: 'address-loading',
    linkId: 'address-loading',
    variant: 'noEndIcon',
  });

  const getLocationItem = (deliveryLocation: DeliveryLocation) => ({
    elements: [
      <LocationIcon
        key={`delivery-icon-${deliveryLocation.DeliveryLocationId}`}
      />,
      getLocationAddress(deliveryLocation),
    ],
    key: `item-user-location-${deliveryLocation.DeliveryLocationId}`,
    linkCallback: () =>
      trackAddressSelected(deliveryLocation.DeliveryLocationId),
    linkId: `item-user-location-${deliveryLocation.DeliveryLocationId}`,
    linkPath: urlUtils.getDeliveryLocation(
      deliveryLocation.DeliveryLocationId,
      deliveryOptions,
      preselectedStoreVrid,
    ),
  });

  const getNewLocationItem = () => ({
    elements: [
      <AddIcon key="new-location-icon" />,
      <Typography
        data-fd="new-location-button"
        key="new-location-label"
        className={classes.locationItem}
      >
        <em>{translate('Delivery_button_newLocation')}</em>
      </Typography>,
    ],
    key: 'item-new-location',
    linkId: 'add-new-address',
    linkPath: '/delivery/new',
    variant: 'noEndIcon',
  });

  const getLogInItem = () => ({
    elements: [
      <ProfileIcon key="login-icon" />,
      <div key="login-container" className={classes.locationItem}>
        <Typography key="login-label">
          <em>{translate('Login')}</em>
        </Typography>
        <Typography
          key="login-description"
          variant="body2"
          className="secondary"
        >
          {translate('Delivery_Button_Login')}
        </Typography>
      </div>,
    ],
    key: 'item-login',
    linkId: 'login',
    linkPath: '/phone',
    variant: 'noEndIcon',
  });

  const getNavListItems = () => {
    const items = [];
    if (!deliveryLocations) {
      items.push(getLocationLoadingItem);
    } else {
      deliveryLocations.data.forEach((location) =>
        items.push(getLocationItem(location)),
      );
    }

    items.push(getNewLocationItem());

    if (!isLoggedIn) {
      items.push(getLogInItem());
    }

    return items;
  };

  const renderBackLink = (): JSX.Element => {
    const isNewUserSession = urlUtils.checkIsNewUserSession();
    const isFromExternalSite = !isNewUserSession && preselectedStoreVrid;

    if (isFromExternalSite) {
      const navigationHistoryLength = urlUtils.getUserSessionHistoryLength();
      const goBackExternalSite = (): void => {
        hashHistory.go(-navigationHistoryLength);
        trackBackSelected();
      };

      return (
        <BackLink
          label={translate('Back') as string}
          linkPath=""
          onClick={goBackExternalSite}
        />
      );
    }

    if (appConfig.PickupEnabled) {
      return (
        <BackLink
          label={translate('Back') as string}
          linkPath="/where"
          onClick={trackBackSelected}
        />
      );
    }

    if (lastRestaurantUrl !== '/') {
      return (
        <BackLink
          label={translate('Menu') as string}
          linkPath={lastRestaurantUrl}
          onClick={trackBackSelected}
        />
      );
    }

    return null;
  };

  const handleOnClickAboutMyData = (): void => {
    setShowAboutMyData(!showAboutMyData);
  };

  return (
    <>
      <Page pageName="delivery-select-location">
        <PageHeader showProfileMenu title={translate('Deliver_to') as string}>
          {renderBackLink()}
        </PageHeader>

        <article className="fd-content__main">
          <NavList
            items={getNavListItems()}
            className={classes.deliveryNavList}
          />

          <div className={classes.aboutMyData}>
            <Button
              className="noUnderline"
              onClick={handleOnClickAboutMyData}
              variant="text"
            >
              {translate('About_my_data')}
            </Button>
          </div>
        </article>
      </Page>
      {showAboutMyData && (
        <AboutMyDataDialog
          handleOnClick={handleOnClickAboutMyData}
          showDialog={showAboutMyData}
          translate={translate}
        />
      )}

      {customer.status === 'success' && !!customer.data.EmailAddress && (
        <CampaignConsentDialog customer={customer} />
      )}
    </>
  );
}
